import { useState } from 'react';
import { Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import {
  StripePriceMapping,
  StripePriceMappingDraft,
  useCreateStripePriceMapping,
  useDeleteStripePriceMapping,
  useRefreshStripePrices,
  useStripePriceMappings,
  useStripeProductStatus,
  useToggleStripeProduct,
  useUpdateStripePriceMapping,
} from '../../queries/stripePriceMappings';
import { StripePriceMappingFormDialog } from './StripePriceMappingFormDialog';
import { SyncWithStripeDialog } from './SyncWithStripeDialog';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Checkbox } from '../../components/ui/Checkbox';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const EMPTY_DRAFT: StripePriceMappingDraft = { billing_cycle: 'weekly', currency: 'aud' };
const CURRENCY = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

export function StripePriceMappingsPage() {
  const { data: mappings = [], isLoading } = useStripePriceMappings();
  const { data: packages = [] } = useSubscriptionPackages();
  const { data: productStatus } = useStripeProductStatus();
  const create = useCreateStripePriceMapping();
  const update = useUpdateStripePriceMapping();
  const remove = useDeleteStripePriceMapping();
  const refresh = useRefreshStripePrices();
  const toggleProduct = useToggleStripeProduct();
  const { success, error } = useToast();

  const [editing, setEditing] = useState<StripePriceMappingDraft | null>(null);
  const [showSync, setShowSync] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<StripePriceMapping | null>(null);

  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    if (!editing) return;
    setFormError(null);
    const isEdit = !!editing.id;
    const onSettled = {
      onSuccess: () => {
        setEditing(null);
        success(isEdit ? 'Mapping updated.' : 'Mapping created.');
      },
      onError: (err: unknown) => setFormError(extractErrorMessage(err, 'Save failed.')),
    };
    if (editing.id) {
      update.mutate({ ...editing, id: editing.id }, onSettled);
    } else {
      create.mutate(editing, onSettled);
    }
  };

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success('Mapping deleted.');
        setDeleting(null);
      },
      onError: (err: unknown) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const handleRefresh = () =>
    refresh.mutate(undefined, {
      onSuccess: () => success('Refreshed prices from Stripe.'),
      onError: (err: unknown) => error(extractErrorMessage(err, 'Refresh failed.')),
    });

  const handleToggleProduct = (productId: string, active: boolean) =>
    toggleProduct.mutate(
      { productId, active },
      {
        onSuccess: () => success(active ? 'Product activated in Stripe.' : 'Product archived in Stripe.'),
        onError: (err: unknown) => error(extractErrorMessage(err, 'Toggle failed.')),
      },
    );

  const columns: DataTableColumn<StripePriceMapping>[] = [
    { key: 'package', header: 'Package', priority: true, render: (m) => <span className="font-medium">{m.package_name ?? m.package_code}</span> },
    { key: 'cycle', header: 'Cycle', priority: true, render: (m) => <span className="text-muted-foreground @2xl:text-foreground">{m.billing_cycle}</span> },
    { key: 'price', header: 'Price', priority: false, render: (m) => <span className="tabular-nums">{CURRENCY.format(m.price_amount)}</span> },
    { key: 'priceId', header: 'Stripe price ID', priority: false, render: (m) => <span className="font-mono text-xs text-muted-foreground">{m.stripe_price_id || '—'}</span> },
    {
      key: 'productActive',
      header: 'Product active',
      priority: false,
      render: (m) => {
        const product = productStatus?.productStatuses[m.stripe_product_id];
        return product ? (
          <Checkbox
            checked={product.active}
            disabled={toggleProduct.isPending}
            onChange={(e) => handleToggleProduct(m.stripe_product_id, e.target.checked)}
            aria-label={`${m.package_name ?? m.package_code} product active`}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    { key: 'synced', header: 'Last synced', priority: false, render: (m) => <span className="text-muted-foreground">{m.last_synced_at ? DATE_FORMATTER.format(new Date(m.last_synced_at)) : 'Never'}</span> },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (m) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              setEditing({
                id: m.id,
                package_id: m.package_id,
                billing_cycle: m.billing_cycle,
                stripe_product_id: m.stripe_product_id,
                stripe_price_id: m.stripe_price_id,
                price_amount: m.price_amount,
                currency: m.currency,
              })
            }
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleting(m)} className="text-muted-foreground hover:text-destructive" title="Delete">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Stripe Price Mappings"
        description="Keeps each package's Stripe product/price in sync with pricing."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refresh.isPending} title="Read-only against Stripe — repairs stale price IDs in our database">
              {refresh.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh prices
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSync(true)}>
              Sync with Stripe
            </Button>
            <Button onClick={() => setEditing({ ...EMPTY_DRAFT })}>
              <Plus className="h-4 w-4" />
              Add mapping
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable columns={columns} data={mappings} getRowKey={(m) => String(m.id)} emptyMessage="No mappings yet." />
      )}

      {editing && (
        <StripePriceMappingFormDialog
          draft={editing}
          onChange={setEditing}
          onCancel={() => {
            setEditing(null);
            setFormError(null);
          }}
          onSave={handleSave}
          saving={saving}
          error={formError}
          packages={packages}
        />
      )}
      {showSync && <SyncWithStripeDialog onClose={() => setShowSync(false)} />}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete price mapping?"
        description={`Removes the ${deleting?.billing_cycle} mapping for ${deleting?.package_name ?? deleting?.package_code} from our database only — it does not archive anything in Stripe. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
